import { inject, Injectable } from '@angular/core';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { RRol } from '@cumlaude/service-contract';
import { RestService } from '@cumlaude/shared-services';
import { UserService } from './user.service';

const PRODUCT_TAG = 'CumLaude';

const ROL_TAG: Record<RRol, string> = {
	[RRol.DOCENT]: 'CL_Docent',
	[RRol.SECTIELEIDER]: 'CL_Sectieleider',
	[RRol.SCHOOLBREED]: 'CL_Schoolbreed',
	[RRol.BEHEERDER]: 'CL_Beheerder',
	[RRol.TEAMLEIDER]: 'CL_Teamleider',
	[RRol.MENTOR]: 'CL_Mentor',
	[RRol.BESTUUR]: 'CL_Bestuur',
	[RRol.AANWEZIGHEID]: 'CL_Aanwezigheid',
};

/**
 * Tags die een feedbackvorm er bovenop mag zetten; deze moeten in Productboard bestaan.
 */
export const FEEDBACK_TAG = {
	NAVIGATIE: 'CL_navigatie_feedback',
} as const;

export type FeedbackTag = (typeof FEEDBACK_TAG)[keyof typeof FEEDBACK_TAG];

export interface Feedback {
	title: string;
	extraTags?: FeedbackTag[];
	/**
	 * Vrije tekst boven de gebruikersgegevens.
	 */
	intro?: string;
	/**
	 * Tekst onder de gebruikersgegevens.
	 */
	outro?: string;
}

/**
 * Verstuurt feedback als note naar Productboard, aangevuld met naam, instelling en rol van de gebruiker.
 */
@Injectable({
	providedIn: 'root',
})
export class FeedbackService {
	private readonly restService = inject(RestService);
	private readonly userService = inject(UserService);

	async post(feedback: Feedback): Promise<void> {
		const account = await firstValueFrom(this.userService.myAccount$);
		const rol = await firstValueFrom(this.userService.rolChanged$);

		const gebruiker = `Naam: ${account.naam}\nInstelling: ${account.instelling.naam}`;
		const content = [feedback.intro, gebruiker, feedback.outro].filter((deel) => deel !== undefined).join('\n\n');
		const tags = [ROL_TAG[rol], PRODUCT_TAG, ...(feedback.extraTags ?? [])];

		await lastValueFrom(this.restService.postFeedback({ title: feedback.title, content, tags }));
	}
}
